import React from 'react';
import { Link2, X, CalendarDays } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

export const linkedDocPreviewText = (p, txCurrency) => {
  if (!p) return null;
  const cur = p.currency || txCurrency;
  const parts = [];
  if (p.total != null) parts.push(formatCurrency(p.total, cur));
  if (p.balance_due != null && p.balance_due > 0 && p.balance_due !== p.total) {
    parts.push(`${formatCurrency(p.balance_due, cur)} due`);
  }
  if (p.due_date) parts.push(`due ${p.due_date}`);
  return parts.join(' · ') || null;
};

const typeLabel = (t) => ({ invoice: 'Invoice', bill: 'Bill' }[t] || 'Document');

/* Blue chip shown on both Review-Needed and In-Books cards. When the backend
   supplies `linked_doc_preview` we render amount + due date under the ref so
   the user can confirm the match without opening the document. */
export const LinkedDocChip = ({ item, onUnlink, testIdPrefix = 'card', txCurrency }) => {
  const p = item.linked_doc_preview || null;
  const ref = item.matched_document_ref || item.document_number || item.created_document_ref || p?.ref;
  if (!ref && !p) return null;

  const total = p?.total != null ? formatCurrency(p.total, p.currency || txCurrency) : null;
  const dueSuffix = p?.due_date ? ` · Due ${p.due_date}` : '';
  const overdue = p?.status === 'overdue';
  const tooltip = p
    ? `${typeLabel(p.type)} ${p.ref || ref}${p.counterparty ? ` · ${p.counterparty}` : ''}${total ? ` · ${total}` : ''}${dueSuffix}${p.status ? ` · ${p.status}` : ''}`
    : `Linked document ${ref}`;

  return (
    <span
      className="inline-flex items-start gap-1 px-1.5 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[10px] text-blue-700 max-w-[240px]"
      data-testid={`${testIdPrefix}-linked-${item.id}`}
      title={tooltip}
    >
      <Link2 className="w-3 h-3 shrink-0 mt-[1px]" />
      <span className="flex flex-col min-w-0 leading-tight">
        <span className="truncate font-medium">{p?.ref || ref}</span>
        {p && (total || p.due_date) && (
          <span
            className="flex items-center gap-1 text-[9px] text-blue-600/90 whitespace-nowrap"
            data-testid={`${testIdPrefix}-linked-preview-${item.id}`}
          >
            {total && <span className="font-semibold">{total}</span>}
            {p.due_date && (
              <span className={`inline-flex items-center gap-0.5 ${overdue ? 'text-red-600 font-semibold' : ''}`}>
                <CalendarDays className="w-2.5 h-2.5" />
                {overdue ? 'Overdue ' : 'Due '}{p.due_date}
              </span>
            )}
          </span>
        )}
      </span>
      {onUnlink && (
        <button
          type="button"
          className="ml-0.5 hover:text-red-600 shrink-0"
          onClick={(e) => { e.stopPropagation(); onUnlink(item); }}
          title="Unlink document"
          data-testid={`${testIdPrefix}-unlink-${item.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};

export default LinkedDocChip;
